import MessagesViewportRequest  from "../Entities/MessagesViewportRequest";
import MessagesFullSearch       from "../Entities/Messages/MessagesFullSearch";
import MailboxesIds             from "../Helpers/MailboxesIds";
import HTTPService              from "@/js/Shared/Services/HTTP/HTTPService";
import HTTPResponseEntity       from "@/js/Shared/Services/HTTP/Entities/HTTPResponseEntity";
import SAPOMailConfig           from "@/SAPOMailConfig";
import MessageItem from "../Entities/Message/MessageItem";

export {ViewportHTTPServiceSingleton as default};

/**
* Middle Man between aplication models and horde api
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ViewportHTTPService {
  /**
   * HTTP Endpoints
   *
   * @private
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  endpoints = SAPOMailConfig.client.endpoints;

  /**
   * Returns main folders index entity, sub folders and main folders in the same index
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesViewportRequest} viewport  messages viewport request object
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchViewport(viewport, zone = null,) {
    let data = {
      view     : viewport.view,
      viewport : JSON.stringify(viewport),
    }

    let path = this.endpoints.imp_services + '/viewport';
  
    if (zone !== null) {
      path += `?zone=${zone}`;
    }

    return await HTTPService.oldPost(path, data, 'messages_fetch_' + data.view);
  }

  /**
   * Calls the server to retrieve message from a given folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesViewportRequest} viewport  messages viewport request object
   * @param {String}                  message_id message id
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchMessage(viewport, message_id) {
    let data     = {
      buid        : message_id,
      flag_config : 1,
      is_list     : 1,
      preview     : 1,
      view        : viewport.view,
      viewport    : JSON.stringify(viewport),
    }

    return await HTTPService.oldPost(
      `${this.endpoints.imp_services}/showMessage`,
       data,
      `message_fetch_${viewport.view}_${message_id}`
    );
  }

  /**
   * Calls the server to send an mdn receipt
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id  folder id
   * @param {String} message_id message id
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async sendMDNReceipt(folder_id, message_id) {
    let data     = {
      buid        : message_id,
      view        : folder_id,
    }

    return await HTTPService.oldPost(
      this.endpoints.imp_services + '/sendMDN',
      data,
      `message_mdn_${folder_id}_${message_id}`
    );
  }

  /**
   * Calls the server to retrieve a message source
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id  folder id
   * @param {String} message_id message id
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchMessageSource(folder_id, message_id) {
    return await HTTPService.get(
      `${this.endpoints.imp_view}?actionID=view_source&id=0&buid=${message_id}&mailbox=${folder_id}`,
    );
  }

  /**
   * Calls the server to retrieve a message print version
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message   message item
   * @param {String}     folder_id folder id
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchMessagePrint(message, folder_id) {
    return await HTTPService.get(
      this.getMessagePrintURL(message, folder_id),
      true,
      true,
    );
  }

  /**
   * Builds the message print URL
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message   message item
   * @param {String}      folder_id folder id
   *
   * @return {string} message print URL
   */
  getMessagePrintURL(message, folder_id) {
    return `${this.endpoints.imp_view}?actionID=print_attach&id=${message.print_id}&buid=${message.id}&mailbox=${folder_id}&sp=1&view_token=${SAPOMailConfig.server.security.csrf_token}`;
  }

  /**
   * Call the server to get the folder id for the given search criteria
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesFullSearch} criteria full search criteria
   * @param {Array}              mboxes   mboxes to search for
   *
   * @return {Promise.<string|null>} search folder id or null if error
   */
  async fetchSearchCriteriaFolder(criteria, mboxes) {
    let criteria_form = [];
    let mboxes_form   = {
      mbox : mboxes,
    }

    if (criteria.subject) {
      this.buildSearchCriteriaFilter(criteria_form, 'subject', criteria.subject, false);
    }

    if (criteria.from) {
      this.buildSearchCriteriaFilter(criteria_form, 'from', criteria.from, false);
    }

    if (criteria.to) {
      this.buildSearchCriteriaFilter(criteria_form, 'to', criteria.to, false);
    }

    if (criteria.body_contain) {
      this.buildSearchCriteriaFilter(criteria_form, 'body', criteria.body_contain, false);
    }

    if (criteria.body_exclude) {
      this.buildSearchCriteriaFilter(criteria_form, 'body', criteria.body_exclude, true);
    }

    if (criteria.flag_flagged !== undefined) {
      this.buildSearchCriteriaFilter(criteria_form, 'flag', '\\flagged', criteria.flag_flagged);
    }

    if (criteria.flag_read !== undefined) {
      this.buildSearchCriteriaFilter(criteria_form, 'flag', '\\seen', !criteria.flag_read);
    }

    if (criteria.filter_attachments !== undefined) {
      this.buildSearchCriteriaFilter(criteria_form, 'attach', null, criteria.filter_attachments);
    }

    if (criteria.filter_size_type) {
      this.buildSearchCriteriaFilter(criteria_form, criteria.filter_size_type, criteria.filter_size_value * 1024);
    }

    if (criteria.filter_date_start && criteria.filter_date_end) {
      this.buildSearchCriteriaRange(criteria_form, 'date_range', criteria.filter_date_start, criteria.filter_date_end, 0);
    }

    let data = {
      criteria_form : JSON.stringify(criteria_form),
      mboxes_form   : JSON.stringify(mboxes_form),
    }

    return await HTTPService.post(this.endpoints.imp_search + '?page=search', data, 'messages_fetch_search_criteria');
  }

  /**
   * Move Messages
   *
   * Moves Messages from one folder to another
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesViewportRequest} viewport       messages viewport request object
   * @param {String}                  destination_id destination folder id
   * @param {Array}                   messages       messages ids to be moved
   *
   * @return {Promise<Boolean>}
   */
  async moveMessages(viewport, destination_id, messages) {
    let data = {
      buid     : MailboxesIds.getIdsAsString(messages),
      mboxto   : destination_id,
      view     : viewport.view,
      viewport : JSON.stringify(viewport),
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/moveMessages', data);
  }

  /**
   * Delete Messages
   *
   * This method has more options i the backen than just move to trash,
   * it can delete permanetly and do other fancy things.
   * The drawback is that this method wont return an updated count on the trash folder
   * Since our delete is just a move to trash, we can safely use moveMessages method to move messages to trash
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesViewportRequest} viewport messages viewport request object
   * @param {Array}                   messages messages ids to be moved
   *
   * @return {Promise<Boolean>}
   */
  async deleteMessages(viewport, messages) {
    let data  = {
      buid     : MailboxesIds.getIdsAsString(messages),
      view     : viewport.view,
      viewport : JSON.stringify(viewport),
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/deleteMessages', data);
  }

  /**
   * Flag mailbox messages
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesViewportRequest} viewport  messages viewport request object
   * @param {Array}                   messages  list of messages id
   * @param {Array}                   flags     list of flags
   * @param {bool}                    add       true adds flag, false remove flags
   *
   * @return {Promise.<boolean>}
   */
  async flagMessages(viewport, messages, flags, add) {
    let data     = {
      add      : add ? 1 : 0,
      buid     : MailboxesIds.getIdsAsString(messages),
      flags    : JSON.stringify(flags),
      view     : viewport.view,
      viewport : JSON.stringify(viewport),
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/flagMessages', data);
  }

  /**
   * Report Spam
   *
   * Reports Messages as Spam and moves them to spam folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesViewportRequest} viewport  messages viewport request object
   * @param {Array}                   messages  list of messages id
   * @param {boolean}                 is_spam   true, is spam, false is inocent
   *
   * @return {Promise.<boolean>}
   */
  async markSpam(viewport, messages, is_spam) {
    let data = {
      buid     : MailboxesIds.getIdsAsString(messages),
      spam     : is_spam === true ? 1 : 0,
      view     : viewport.view,
      viewport : JSON.stringify(viewport),
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/reportSpam', data);
  }

  /**
   * Builds a search criteria filter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Array}   criteria fsearch criteria
   * @param {string}  field    field name
   * @param {string}  value    field value
   * @param {boolean} exclude  true does not contain, false contains
   *
   * @return {Object}
   */
  buildSearchCriteriaFilter(criteria, field, value, exclude) {
    criteria.push({
      n: exclude,
      t: field,
      v: value,
    });

    return criteria;
  }

  /**
   * Builds a search criteria Range
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Array}   criteria fsearch criteria
   * @param {string}  field    field name
   * @param {string}  start    range start
   * @param {string}  end      range end
   * @param {boolean} exclude  true does not contain, false contains
   *
   * @return {Object}
   */
   buildSearchCriteriaRange(criteria, field, start, end, exclude) {
    criteria.push({
      n: exclude,
      t: field,
      b: start + 'T00:00:00.000Z',
      e: end   + 'T23:59:00.000Z',
    });

    return criteria;
  }
}

/**
 * @type {ViewportHTTPService}
 */
let ViewportHTTPServiceSingleton = new ViewportHTTPService();